import { Box, Stack, Typography } from '@mui/material';
import React from 'react';

import { colors } from '../../../components/theme/theme';

export interface AmortizationMobileRow {
    date: string;
    eventType: string;
    amount: string;
    principal: string;
    interest: string;
    balance: string;
    isTotal?: boolean;
    isGrandTotal?: boolean;
}

interface AmortizationMobileCardProps {
    row: AmortizationMobileRow;
}

/**
 * Mobile/tablet-only card rendering of a single amortization schedule row.
 * Desktop keeps using CommonTable in amortizationSchedule.tsx.
 */
export const AmortizationMobileCard: React.FC<AmortizationMobileCardProps> = ({ row }) => {
    const isPaid = row.eventType?.toLowerCase() === 'paid';
    const dotColor = isPaid ? colors.successPreset[10] : colors.greyPreset[100];
    const isSummaryRow = row.isTotal || row.isGrandTotal;
    const summaryBorderColor = row.isGrandTotal ? colors.successPreset[10] : colors.warningPreset[40];
    const summaryBackgroundColor = row.isGrandTotal ? colors.successPreset[40] : colors.warningPreset[60];

    return (
        <Box
            sx={{
                border: `1px solid ${isSummaryRow ? summaryBorderColor : colors.greyPreset[30]}`,
                borderRadius: 1.5,
                backgroundColor: isSummaryRow ? summaryBackgroundColor : colors.fullWhite,
                overflow: 'hidden',
                p: 1.5,
            }}
        >
            {isSummaryRow ? (
                <Typography variant="subtitle2" fontWeight={700}>
                    {row.isGrandTotal ? row.eventType : `${row.date} ${row.eventType}`}
                </Typography>
            ) : (
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                    <Stack gap={0.5}>
                        <Stack direction="row" gap={0.75} alignItems="center">
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: dotColor }} />
                            <Typography variant="caption" color={colors.greyPreset[100]} fontWeight={700}>
                                DATE
                            </Typography>
                        </Stack>
                        <Typography variant="subtitle2" fontWeight={700}>
                            {row.date}
                        </Typography>
                    </Stack>
                    <Stack gap={0.5} alignItems="flex-end">
                        <Typography variant="caption" color={colors.greyPreset[100]} fontWeight={700}>
                            SOURCE
                        </Typography>
                        <Typography variant="body2" fontWeight={500} textAlign="right">
                            {row.eventType}
                        </Typography>
                    </Stack>
                </Stack>
            )}

            <Box
                sx={{
                    mt: 1.5,
                    p: 1.25,
                    borderRadius: 1,
                    backgroundColor: isSummaryRow ? colors.fullWhite : colors.greyPreset[90],
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: 1,
                }}
            >
                {[
                    { label: 'PAYMENT', value: row.amount },
                    { label: 'PRINCIPAL', value: row.principal },
                    { label: 'INTEREST', value: row.interest },
                    { label: 'BALANCE', value: row.balance, blue: true },
                ].map(cell => (
                    <Stack key={cell.label} gap={0.25}>
                        <Typography variant="caption" color={colors.greyPreset[100]} fontWeight={700} noWrap>
                            {cell.label}
                        </Typography>
                        <Typography
                            variant="body2"
                            fontWeight={700}
                            color={cell.blue ? colors.primaryLight[100] : colors.fullBlack}
                            noWrap
                        >
                            {cell.value}
                        </Typography>
                    </Stack>
                ))}
            </Box>
        </Box>
    );
};
